import { useRef, useState } from 'react'
import { AlertCircle, Camera, CheckCircle2, ImagePlus, Loader2, RotateCw } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import {
  MEDIA_ACCEPT,
  MediaUploadError,
  UploadCaps,
  guestUploadHandlers,
  prepareFile,
  uploadPrepared,
} from '../lib/media'
import { formatBytes } from '../lib/media'
import { burstConfetti } from './celebration'

type InviteTheme = {
  cardBg: string
  textColor: string
  accent: string
}

type QueueStatus = 'preparing' | 'uploading' | 'finalizing' | 'done' | 'error'

type QueueItem = {
  id: string
  file: File
  label: string
  size: number
  status: QueueStatus
  progress: number // 0..1 of the PUT
  error?: string
}

/**
 * Guest uploader for the R2-backed media vault (the /invite/<link>/share
 * page). Files go straight to the bucket via presigned PUTs — images are
 * compressed on-device first and every upload gets a progress bar with
 * automatic retries for flaky mobile connections. Styling follows the
 * invitation theme so it drops into themed invite surfaces too.
 */
const GuestMediaUpload = ({
  inviteLink,
  theme,
  lang,
  defaultName,
  caps,
}: {
  inviteLink: string
  theme: InviteTheme
  lang: string
  defaultName?: string
  caps?: UploadCaps
}) => {
  const tr = (en: string, ka: string) => (lang === 'ka' ? ka : en)
  const isDarkTheme = theme.textColor === 'text-white'

  const [name, setName] = useState(defaultName || '')
  const [queue, setQueue] = useState<QueueItem[]>([])
  const [busy, setBusy] = useState(false)
  const [sharedCount, setSharedCount] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const handlers = guestUploadHandlers(inviteLink)

  const patchItem = (id: string, patch: Partial<QueueItem>) =>
    setQueue((q) => q.map((item) => (item.id === id ? { ...item, ...patch } : item)))

  const errorMessage = (err: any): string => {
    if (err instanceof MediaUploadError && err.status === 413) {
      return tr('Too large for this album', 'ზედმეტად დიდია ამ ალბომისთვის')
    }
    return (
      err?.response?.data?.detail ||
      (err instanceof MediaUploadError ? err.message : null) ||
      tr('Upload failed — tap to retry', 'ატვირთვა ვერ მოხერხდა — სცადეთ თავიდან')
    )
  }

  const uploadOne = async (item: QueueItem, uploaderName: string) => {
    patchItem(item.id, { status: 'preparing', progress: 0, error: undefined })
    try {
      const prepared = await prepareFile(item.file)
      patchItem(item.id, { status: 'uploading', size: prepared.blob.size })
      await uploadPrepared(
        handlers,
        prepared,
        uploaderName,
        (fraction) => {
          patchItem(item.id, { progress: fraction, status: fraction >= 1 ? 'finalizing' : 'uploading' })
        },
        caps,
      )
      patchItem(item.id, { status: 'done', progress: 1 })
      return true
    } catch (err: any) {
      patchItem(item.id, { status: 'error', error: errorMessage(err) })
      return false
    }
  }

  // Sequential on purpose: parallel multi-GB uploads starve each other on
  // mobile connections, and progress per file stays honest.
  const runQueue = async (items: QueueItem[]) => {
    setBusy(true)
    const uploaderName = name || defaultName || ''
    let ok = 0
    for (const item of items) {
      if (await uploadOne(item, uploaderName)) ok += 1
    }
    setBusy(false)
    if (ok > 0) {
      setSharedCount((n) => n + ok)
      burstConfetti()
      toast.success(
        lang === 'ka'
          ? `${ok} ფაილი გაზიარდა — მადლობა!`
          : `${ok} file${ok > 1 ? 's' : ''} shared — thank you!`,
      )
    }
  }

  const handleFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return
    const items: QueueItem[] = Array.from(list).map((file) => ({
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      file,
      label: file.name,
      size: file.size,
      status: 'preparing',
      progress: 0,
    }))
    if (fileInputRef.current) fileInputRef.current.value = ''
    // Keep finished rows visible, replace older errors to avoid clutter.
    setQueue((q) => [...q.filter((i) => i.status === 'done'), ...items])
    runQueue(items)
  }

  const retryItem = (item: QueueItem) => {
    if (busy) return
    setBusy(true)
    uploadOne(item, name || defaultName || '').then((ok) => {
      setBusy(false)
      if (ok) {
        setSharedCount((n) => n + 1)
        burstConfetti()
      }
    })
  }

  const maxImageMb = Math.round((caps?.maxImageBytes || 25 * 1024 * 1024) / (1024 * 1024))
  const maxVideoGb = (caps?.maxVideoBytes || 4 * 1024 ** 3) / 1024 ** 3

  const mutedText = isDarkTheme ? 'text-gray-400' : 'text-[#4a3d66] dark:text-[#d9d0ec]'

  return (
    <div
      className={`${
        theme.cardBg === 'bg-zinc-900' ? `${theme.cardBg} border border-white/10` : 'glass-panel'
      } rounded-2xl p-6 animate-slide-up`}
      data-testid="guest-media-upload"
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className={`w-10 h-10 rounded-2xl flex items-center justify-center ${
            isDarkTheme ? 'bg-white/10' : 'bg-[#fdeee9] dark:bg-[#281b47]'
          }`}
        >
          <Camera className="w-5 h-5" style={{ color: theme.accent }} />
        </div>
        <div>
          <h2 className={`text-xl font-semibold ${theme.textColor} font-display tracking-tight`}>
            {tr('Share your photos & videos', 'გააზიარეთ ფოტოები და ვიდეოები')}
          </h2>
          <p className={`text-sm ${mutedText}`}>
            {tr(
              'They upload straight from your phone to the event album.',
              'ისინი პირდაპირ თქვენი ტელეფონიდან იტვირთება ღონისძიების ალბომში.',
            )}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <Label
            className={`text-[13px] font-medium mb-1.5 block ${
              isDarkTheme ? 'text-gray-300' : 'text-[#1b1033]/70 dark:text-[#c3b8dd]'
            }`}
          >
            {tr('Your name (optional)', 'თქვენი სახელი (არასავალდებულო)')}
          </Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={tr('So the hosts know who shared it', 'რომ მასპინძლებმა იცოდნენ ვინ გააზიარა')}
            maxLength={80}
            className={`h-11 rounded-xl ${
              isDarkTheme ? 'bg-white/5 border-white/15 text-white placeholder:text-gray-500' : ''
            }`}
            data-testid="guest-media-name-input"
          />
        </div>

        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          className="w-full h-12 rounded-full text-white font-semibold"
          style={{ backgroundColor: theme.accent }}
          data-testid="guest-media-upload-btn"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {tr('Uploading…', 'იტვირთება…')}
            </>
          ) : (
            <>
              <ImagePlus className="w-4 h-4 mr-2" />
              {tr('Add photos or videos', 'ფოტოების ან ვიდეოების დამატება')}
            </>
          )}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={MEDIA_ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="guest-media-input"
        />

        {queue.length > 0 && (
          <ul className="space-y-2" data-testid="guest-media-queue">
            {queue.map((item) => (
              <li
                key={item.id}
                className={`rounded-xl px-3 py-2 text-sm ${
                  isDarkTheme ? 'bg-white/5' : 'bg-white/60 dark:bg-white/5'
                }`}
              >
                <div className="flex items-center gap-2">
                  {item.status === 'done' ? (
                    <CheckCircle2 className="w-4 h-4 shrink-0 text-green-500" />
                  ) : item.status === 'error' ? (
                    <AlertCircle className="w-4 h-4 shrink-0 text-red-500" />
                  ) : (
                    <Loader2 className="w-4 h-4 shrink-0 animate-spin" style={{ color: theme.accent }} />
                  )}
                  <span className={`truncate flex-1 ${theme.textColor}`}>{item.label}</span>
                  <span className={`shrink-0 text-xs ${mutedText}`}>
                    {item.status === 'uploading'
                      ? `${Math.round(item.progress * 100)}%`
                      : item.status === 'preparing'
                        ? tr('Preparing…', 'მზადდება…')
                        : item.status === 'finalizing'
                          ? tr('Saving…', 'ინახება…')
                          : formatBytes(item.size)}
                  </span>
                  {item.status === 'error' && (
                    <button
                      onClick={() => retryItem(item)}
                      className="shrink-0 p-1 rounded-full hover:bg-black/10 dark:hover:bg-white/10"
                      title={tr('Retry', 'თავიდან ცდა')}
                      data-testid="guest-media-retry"
                    >
                      <RotateCw className="w-4 h-4" style={{ color: theme.accent }} />
                    </button>
                  )}
                </div>
                {(item.status === 'uploading' || item.status === 'finalizing') && (
                  <div className="mt-1.5 h-1.5 rounded-full bg-black/10 dark:bg-white/10 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{ width: `${Math.round(item.progress * 100)}%`, backgroundColor: theme.accent }}
                    />
                  </div>
                )}
                {item.status === 'error' && item.error && (
                  <p className="mt-1 text-xs text-red-500">{item.error}</p>
                )}
              </li>
            ))}
          </ul>
        )}

        {sharedCount > 0 && (
          <p
            className="flex items-center justify-center gap-1.5 text-sm font-medium text-green-600 dark:text-green-400"
            data-testid="guest-media-shared-count"
          >
            <CheckCircle2 className="w-4 h-4" />
            {lang === 'ka'
              ? `თქვენ გააზიარეთ ${sharedCount} ფაილი`
              : `You've shared ${sharedCount} ${sharedCount === 1 ? 'file' : 'files'}`}
          </p>
        )}

        <p className={`text-center text-[11px] ${isDarkTheme ? 'text-gray-500' : 'text-[#4a3d66]/70 dark:text-[#c3b8dd]/70'}`}>
          {tr(
            `Photos up to ${maxImageMb} MB, videos up to ${maxVideoGb} GB. Only the hosts can see the album.`,
            `ფოტოები ${maxImageMb} MB-მდე, ვიდეოები ${maxVideoGb} GB-მდე. ალბომს მხოლოდ მასპინძლები ხედავენ.`,
          )}
        </p>
      </div>
    </div>
  )
}

export default GuestMediaUpload
